import { useState } from 'react';

import Button from '@ingka/button';
import type { InlineMessageHeadingLevel } from '@ingka/inline-message';
import chevronDownIcon from '@ingka/ssr-icon/paths/chevron-down';
import chevronUpIcon from '@ingka/ssr-icon/paths/chevron-up';
import { translate } from '@repo/phrase';
import type { AssetArticle } from '@repo/types/asset';
import { Heading, Paragraph } from '@repo/ui/typography';

import { ArticleCardItem } from '@/components/article-table/article-card/article-card-item';
import { ArticleCardProduct } from '@/components/article-table/article-card/article-card-product';
import { ArticleCardTextLine } from '@/components/article-table/article-card/article-card-textline';

import { getAssetArticleKey } from '@/utils/article.utils';
import { formatPrice } from '@/utils/translation.utils';

import { BASE_CARD_HEADING_LEVEL } from '../article-card.utils';

import sharedStyles from '@/components/article-table/styles.module.scss';
import styles from './styles.module.scss';

interface Props {
    item: AssetArticle;
    headingLevel?: InlineMessageHeadingLevel;
}

export const ArticleCardGroup = ({ item, headingLevel = BASE_CARD_HEADING_LEVEL }: Props) => {
    const [isCollapsed, setCollapsed] = useState(false);
    const totalPrice = formatPrice(item.totalPrice, item.currency);
    const rowStyles = styles.row_group;

    return (
        <div className={styles.article_table_group}>
            <div className={`${rowStyles} ${sharedStyles.article_table_row}`} data-testid="article-card-group-row">
                <div className={styles.group_action_cell}>
                    {Boolean(item.childItems?.length) && (
                        <Button
                            iconOnly
                            size="small"
                            type="tertiary"
                            ssrIcon={isCollapsed ? chevronDownIcon : chevronUpIcon}
                            onClick={() => {
                                setCollapsed((prev) => !prev);
                            }}
                            aria-label={translate(isCollapsed ? 'article_group_expand' : 'article_group_collapse')}
                            aria-expanded={!isCollapsed}
                        />
                    )}
                </div>
                <div className={styles.group_name_cell}>
                    <Heading className={styles.group_name ?? ''} headingLevel={headingLevel} headingSize="xs">
                        {item.name}
                    </Heading>
                </div>
                <div className={styles.total_price_cell}>
                    <Paragraph className={styles.total_price ?? ''}>{totalPrice}</Paragraph>
                </div>
            </div>
            {!isCollapsed &&
                item.childItems?.map((item) => {
                    if (item.type === 'TEXTLINE') {
                        return <ArticleCardTextLine item={item} key={item.itemId} />;
                    }

                    const isParentItem = item.type === 'PRODUCT' || !item.childItems;

                    return isParentItem ? (
                        <ArticleCardProduct item={item} key={getAssetArticleKey(item)} />
                    ) : (
                        <ArticleCardItem item={item} key={getAssetArticleKey(item)} />
                    );
                })}
        </div>
    );
};
